<template>
	<main :data-test="$route.path">
		<header>
			<div class="relative grid min-h-495 tablet:min-h-720 tabletWide:min-h-696 desktop:min-h-618">
				<div class="tablet:mx-48 desktop:mx-56">
					<div class="pt-16 tablet:pt-24 pb-32 tablet:pb-24 px-16 tablet:px-48 desktop:px-56 max-w-375 tablet:max-w-none desktop:mx-auto desktop:max-w-1440 flex flex-col h-full space-y-24">
						<breadCrumb v-bind="pageContent.pageHeader.breadcrumb" />

						<div class=" flex-1 flex flex-col justify-end tablet:justify-center space-y-24 tablet:space-y-16 tabletWide:space-y-32">
							<h1 class="text-white font-bold space-y-6 tablet:space-y-12 text-40 tablet:text-64">{{ pageContent.pageHeader.title }}</h1>
							<p class="text-white text-18 tablet:text-22">{{ pageContent.pageHeader.headline }}</p>
							<div class="flex items-center tabletWide:items-start desktop:items-center tabletWide:flex-col desktop:flex-row tablet:space-x-32 tabletWide:space-x-16 tabletWide:space-y-32 desktop:space-y-0 ">
								<formItinerary theme="ghost" :showLabels="true" :branch-picker-start-code="pageContent.location.code" :branch-picker-end-code="pageContent.location.code" class="bg-white/40 rounded-GO p-16 w-full tablet:w-8/12 tabletWide:w-full desktop:w-10/12 desktop:p-24 desktopWide:w-9/12" />

								<endorsementsHeader />
							</div>
						</div>
					</div>
				</div>

				<figure class="absolute inset-0 -z-10 rounded-GO overflow-hidden tablet:ml-48 desktop:ml-56">
					<LazyHydrate never>
						<goPicture v-once v-bind="pageContent.pageHeader.picture" :enable-placeholder="false" class="h-full" />
					</LazyHydrate>
				</figure>

				<div class="absolute inset-0 -z-10 rounded-GO overflow-hidden tablet:ml-48 desktop:ml-56 bg-goPageHeaderGradientMobile tablet:bg-goPageHeaderGradientTablet tabletWide:bg-goPageHeaderGradientTabletWide desktop:bg-goPageHeaderGradientDesktop desktopWide:bg-goPageHeaderGradientDesktopWide"></div>
			</div>
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection class="p-16 group bg-goPink hover:bg-goPinkDark transition-colors rounded-GO tablet:rounded-tr-0 cursor-pointer">
					<subscribeBanner />
				</layoutSection>
			</LazyHydrate>

			<whyGoWithUsSection />

			<totalCareSection />

			<LazyHydrate when-visible>
				<layoutSection type="post-reversed">
					<template #main>
						<layoutPost type="start">
							<h2 class="font-bold text-32 tablet:text-40 desktop:text-48 ">
								Car Rental Auckland Locations
							</h2>
							<p class="text-18 desktop:text-22">From a vibrant city centre to stunning landscapes, Auckland is a popular rental car location for both Kiwi and international visitors.</p>
							<p>Pick up a car in Auckland and explore the awesome things to do in New Zealand's largest city, from the iconic Sky Tower, the Auckland Art Gallery or the breathtaking Mount Eden volcano.</p>
							<p>With a rental car location only a stone's throw away from Auckland International Airport and the domestic terminal, you could head out on an epic road trip. Auckland is the gateway to exploring the North Island's majestic scenery.</p>
							<p>For a relaxing weekend, head over to the ferry crossing and pick up a rental car on Waiheke Island, only a short distance from Auckland city centre.</p>
							<p>All our locations are conveniently located to give you the flexibility to hire in Auckland with a hassle-free service.</p>
						</layoutPost>
					</template>
					<template #aside>
						<div class="p-16 tablet:p-0 space-y-32">
							<figure class="relative z-0 tablet:rounded-GO tablet:overflow-hidden">
								<aucklandBranchesGoogleMap v-if="showMap" class="absolute z-50 inset-0" />
								<div class="bg-[#e5e3df]" @mouseover="showGoogleMap" @click="showGoogleMap">
									<button class="absolute z-40 bottom-0 w-full text-center text-12 p-8 bg-white/50 device-mouse:hidden">Tap to interact</button>
									<svg-icon :class="mapMarkerClass" name="pointer" class="map-marker1" />
									<svg-icon :class="mapMarkerClass" name="pointer" class="map-marker2" />
									<svg-icon :class="mapMarkerClass" name="pointer" class="map-marker3" />
									<goPicture v-bind="mapImage" class="relative z-20 transition-opacity duration-1000" :class="[showMap ? 'opacity-0' : 'opacity-50']"/>
									<svg-icon name="loader-mini" class="iconSize-24 text-goPink absolute z-10 inset-1/2 -ml-24 animate-backandforth duration-1000 delay-1000" :class="[showMap ? 'opacity-100' : 'opacity-0']"/>
								</div>
							</figure>
						</div>
					</template>
					<template #below>
						<div class="col-span-12 p-16">
							<div class="grid grid-cols-1 desktop:grid-cols-3 gap-y-32 tablet:gap-x-32 tabletWide:w-8/12 desktop:w-auto">
								<article v-for="branch in pageContent.branches" :key="branch.code" class="space-y-24 tablet:space-y-32">
									<h3 class="font-bold"><span class="text-24 text-goPink">{{ branch.title }}</span></h3>
									<div class="space-y-16">
										<p v-for="detail in branch.details" :key="detail.label" class="text-16">
											<strong class="font-bold">{{ detail.label }}: </strong>{{ detail.value }}
											<nuxt-link v-if="detail.link" :to="detail.link.to" class="link link-goBlue font-bold">{{ detail.link.label }}</nuxt-link>
										</p>
										<p v-if="branch.code === 'AC'" class="text-16">
											Our off-airport location in <nuxt-link class="link link-goBlue font-bold" to="/rental-car-locations/auckland-city/">Auckland City</nuxt-link> centre is conveniently located to give you flexibility between an urban break and a world of adventure. Pick up a small economy car hire to zip through the streets of Auckland CBD or test drive a luxury Tesla car hire.
										</p>
										<p v-else-if="branch.code === 'AA'" class="text-16">
											Get GOing as soon as you land! Get behind the steering wheel of an <nuxt-link class="link link-goBlue font-bold" to="/rental-car-locations/auckland-airport/">Auckland Airport</nuxt-link> rental car and shift into holiday mode. Our rental counter is only a short shuttle drive away which can be booked through our app. With 24-hour pick-up and drop-off available, our Auckland Airport location works for you.
										</p>
										<p v-else class="text-16">
											Conveniently located at the ferry terminal, our <nuxt-link class="link link-goBlue font-bold" to="/rental-car-locations/waiheke/">Waiheke</nuxt-link> location opens you up to a world of stunning greenery, adventures and many quaint towns. Often hailed as one of the most relaxing places in New Zealand, our GO Rentals car hire could be the solution you need for a zen trip.
										</p>
									</div>
								</article>
							</div>
						</div>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<customerQuickFacts />
			</LazyHydrate>

			<dealsSection>
				<template #title>
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Auckland Car Rental Deals and Exclusive Discounts</h2>
				</template>
				<template #intro>
					<p class="text-18 desktop:text-22">Cruise the best <nuxt-link to="/deals/" class="link link-goBlue font-bold">deals</nuxt-link> and <nuxt-link to="/rental-car-relocation/" class="link link-goBlue font-bold">relocations</nuxt-link> for an affordable car rental. Make the most of your New Zealand adventure for less with GO.</p>
				</template>
			</dealsSection>

			<LazyHydrate when-visible>
				<layoutSection type="post">
					<template #main>
						<layoutPost type="left">
							<header>
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Ridiculously simple pick-up & drop-off of our rental cars</h2>
							</header>
							<p class="text-18 desktop:text-22">Auckland is a bustling city waiting for you to explore and we don't want to keep you waiting. Using the GO app or online check-in, you can breeze past the traditional paperwork at pick-up and turn the ignition to GO faster than before. Download our app to cruise through your car rental process.</p>
							<footer>
								<nuxt-link to="/app/" class="button button-goBlueGhost button-normal">
									Download App
								</nuxt-link>
							</footer>
						</layoutPost>
					</template>
					<template #aside>
						<figure class="h-full flex items-center ">
							<videoYouTube class="w-full" v-bind="{
								image: { mobile: { src: '/images/Simple_PU_DO.jpg' }, alt: 'Image of a GO Rentals branch showing its self-service kiosks' },
								title: 'The GO Experience',
								src: 'https://www.youtube.com/embed/KjChqxbIHoY',
								duration: '1:58',
								ratio: '3:2',
							}" />
						</figure>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection type="post">
					<template #main>
						<layoutPost type="full">
							<header>
								<p class="font-bold text-32 tablet:text-40 desktop:text-48">Qualmark Gold</p>
							</header>
							<p class="space-y-16">Qualmark is New Zealand tourism’s official quality assurance organisation, providing a trusted guide to quality travel experiences. In 2023, GO Rentals achieved Qualmark Gold which recognises the best sustainable tourism businesses in New Zealand, with the delivery of exceptional customer experiences an integral part of everything they do.</p>
							<p class="space-y-16">A Gold Sustainable Tourism Award identifies those businesses leading the way in making the New Zealand tourism industry a world class sustainable visitor destination. When you see a business displaying the Qualmark Gold symbol you can be assured that the business has met stringent quality, environmental, economic, social and health & safety criteria - earning New Zealand tourism’s official quality mark.</p>
							<p class="space-y-16">Learn more at <a href="https://www.qualmark.co.nz" class="link link-goBlue font-bold">qualmark.co.nz</a></p>
						</layoutPost>
					</template>
					<template #aside>
						<layoutPost type="center">
							<goPicture
								class="max-w-sm"
								alt="Qualmark gold award logo"
								:mobile="{ src: '/images/qualmarkLogo.jpg', width: 375, height: 250 }"
								:tablet="{ src: '/images/qualmarkLogo.jpg', width: 456, height: 304 }"
								:tabletWide="{ src: '/images/qualmarkLogo.jpg', width: 220, height: 330 }"
								:desktop="{ src: '/images/qualmarkLogo.jpg', width: 456, height: 304 }"
								:desktopWide="{ src: '/images/qualmarkLogo.jpg', width: 456, height: 304 }"
							/>
						</layoutPost>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<googleReviewsWidget :locationWidgetId="pageContent.reviewsWidgetId" :title="`Customer reviews from ${pageContent.pageHeader.title}`" />
			</LazyHydrate>

			<insuranceSection />

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-24 desktop:text-32">Popular Vehicles to Hire in Auckland</h2>
							</header>
							<div class="space-y-16">
								<p>With over 20 years experience in the New Zealand car hire industry, we pride ourselves on selecting a sleek, modern fleet to give you the best ride. From a sustainable electric fleet to various SUV and family cars, we ensure every vehicle will give you a relaxing trip in Auckland.</p>
							</div>
						</div>
						<vehicleGroupsSection class="grid grid-cols-2 gap-16 tablet:grid-cols-3 desktop:grid-cols-7" />
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Auckland Car Rental: The Urban Gateway to Cruising New Zealand</h2>
							</header>
							<div class="tablet:w-8/12 text-18 desktop:text-22">
								<p class="mb-16">Gift yourself the convenience of the open road with a car rental in Auckland. With our <nuxt-link to="/services/one-way-car-rental/" class="link link-goBlue font-bold">one-way rental</nuxt-link> service, you could even pick up a car in Auckland and drop it off at one of our many locations on the North or South Island</p>
								<p>There is nothing better than a sleek car, time on your hands and the stunning New Zealand roads to explore, in our opinion. We're not biased, we swear! But here are our favourite places to explore with an Auckland car hire:</p>
							</div>
						</div>
						<div class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-3 desktop:gap-16 ">
							<div v-for="article in pageContent.expolreArticles" :key="article.name"
								class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<img :src="$cloudFlare.getImage(article.image)" width="432" height="224" loading="lazy" :alt="article.name" class="bg-gray-50 object-cover w-full h-full" />
									</figure>
									<div class="p-16 space-y-16">
										<header>
											<p class="font-bold text-18">{{ article.name }}</p>
										</header>
										<p class="text-14">{{ article.description }}</p>
									</div>
								</article>
							</div>
						</div>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32 tablet:mb-24">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Snap Happy Photo Competition</h2>
							</header>
							<div class="tablet:w-8/12 ">
								<p class="text-18 desktop:text-22 mb-24">We love that our rental cars become part of your travel memories! Submit a happy photo to our <nuxt-link to="/go-snap-happy/" class="link link-goBlue font-bold">snap happy competition</nuxt-link> and be in with a chance to receive $500 back off your booking!</p>
								<p>New Zealand is best explored in a sleek, modern car. Don't take our word for it, check out the happy customers revv'd up about their experience in an Auckland car rental:</p>
							</div>
						</div>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16 ">
							<li v-for="snapHappy in pageContent.snapHappy" :key="snapHappy.name" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<img :src="$cloudFlare.getImage(snapHappy.image)" width="284" height="238" loading="lazy" decoding="async" :alt="snapHappy.alt" class="bg-gray-50 object-cover w-full h-full">
									</figure>
									<div class="p-16 space-y-16">
										<header>
											<h3 class="font-bold">{{ snapHappy.name }}</h3>
										</header>
										<p class="mb-16">{{ snapHappy.text }}˝</p>
									</div>
								</article>
							</li>
						</ul>
					</div>
					<layoutPost type="wide">
						<div class="space-y-16">
							<p>Let’s get GOing on a car rental trip of a lifetime! <a href="#" class="text-goPink">Book today.</a></p>
						</div>
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<secondaryMenu :links="pageContent.secondaryMenu" />
			</LazyHydrate>
		</div>
	</main>
</template>

<script>
import breadCrumb from '~/components/breadCrumb.vue'
import endorsementsHeader from "~/components/endorsementsHeader.vue"
import layoutSection from '~/components/layoutSection.vue'
import layoutPost from '~/components/layoutPost.vue'
import secondaryMenu from "~/components/secondaryMenu.vue"
import { getPageMetaData } from '~/assets/scripts/utilities/getPageMetaData'

export default {
	components: {
		breadCrumb,
		endorsementsHeader,
		layoutSection,
		layoutPost,
		secondaryMenu,
		formItinerary: () => import(/* webpackChunkName: "formItinerary" */ "~/components/formItinerary.vue"),
		subscribeBanner: () => import(/* webpackChunkName: "subscribeBanner" */"~/components/subscribeBanner.vue"),
		aucklandBranchesGoogleMap: () => import(/* webpackChunkName: "aucklandBranchesGoogleMap" */ "~/components/aucklandBranchesGoogleMap.vue"),
		customerQuickFacts: () => import(/* webpackChunkName: "customerQuickFacts" */ "~/components/customerQuickFacts.vue"),
		dealsSection: () => import(/* webpackChunkName: "dealsSection" */"~/components/dealsSection.vue"),
		videoYouTube: () => import(/* webpackChunkName: "videoYouTube" */ "~/components/videoYouTube.vue"),
		vehicleGroupsSection: () => import(/* webpackChunkName: "vehicleGroupsSection" */"~/components/vehicleGroupsSection.vue"),
		whyGoWithUsSection: () => import("~/components/whyGoWithUsSection.vue"),
		totalCareSection: () => import("~/components/totalCareSection.vue"),
		insuranceSection: () => import("~/components/insuranceSection.vue"),
		googleReviewsWidget: () => import("~/components/googleReviewsWidget.vue"),
	},
	metaInfo() {
		const { picture } = this.pageContent.pageHeader

		return {
			title:  this.pageMeta.title,
			meta: [
				{
					vmid: "description",
					name: "description",
					content: this.pageMeta.description,
				},
				{ vmid: "ogtitle", property: "og:title", content: this.pageMeta.title },
				{ vmid: "ogurl", property: "og:url", content: "https://www.gorentals.co.nz/rental-car-locations/car-rental-auckland/" },
			],
			link: [
				{ rel: "canonical", href: "https://www.gorentals.co.nz/rental-car-locations/car-rental-auckland/" },
				{ rel: "preload",	href: this.$cloudFlare.getImage(picture.desktopWide.src,	{ width: picture.desktopWide.width,		height: picture.desktopWide.height }),		as: 'image', media: `(min-width: 100em)`,	fetchpriority: "high" },
				{ rel: "preload",	href: this.$cloudFlare.getImage(picture.desktop.src, 		{ width: picture.desktop.width,			height: picture.desktop.height }),			as: 'image', media: `(min-width: 80em)		and (max-width: ${((100   * 16) - 1) / 16}em)`,	fetchpriority: "high" },
				{ rel: "preload",	href: this.$cloudFlare.getImage(picture.tabletWide.src, 	{ width: picture.tabletWide.width,		height: picture.tabletWide.height }),		as: 'image', media: `(min-width: 62.5em)	and (max-width: ${((80	  * 16) - 1) / 16}em)`,	fetchpriority: "high" },
				{ rel: "preload",	href: this.$cloudFlare.getImage(picture.tablet.src,			{ width: picture.tablet.width,			height: picture.tablet.height }),			as: 'image', media: `(min-width: 43.75em)	and (max-width: ${((62.5  * 16) - 1) / 16}em)`,	fetchpriority: "high" },
				{ rel: "preload",	href: this.$cloudFlare.getImage(picture.mobile.src,			{ width: picture.mobile.width,			height: picture.mobile.height }),			as: 'image', media: `(min-width: 0em) 		and (max-width: ${((43.75 * 16) - 1) / 16}em)`,	fetchpriority: "high" },
			],
			script: [
				{
					type: "application/ld+json",
					json: this.pageContent.structuredData,
				},
			]
		}
	},
	async asyncData(context) {
		const slug = 'car-rental-auckland'
		const [pageMetaData, { default: _pageContent }] = await Promise.all([
			getPageMetaData(context.$strapi, slug),
			import("~/static/data/locations/" + slug),
			context.store.dispatch("reviews/fetchBranch")
		])

		// We learned that by using import the response is being cached by node
		// kind of whats happening here: https://github.com/nodejs/modules/issues/307
		// this is why we deep clone the object
		const pageContent = JSON.parse(JSON.stringify(_pageContent))

		const { title, description } = pageMetaData ?? pageContent.metaInfo
		const pageMeta = {
			title,
			description
		}

		pageContent.location = { ...context.store.getters['location/bySlug']['auckland-city'] }

		const branchesReviews = [
			context.store.getters['reviews/branchBySlug']('auckland-city'),
			context.store.getters['reviews/branchBySlug']('auckland-airport'),
			context.store.getters['reviews/branchBySlug']('waiheke')
		]

		branchesReviews.forEach((branchReview, index) => {
			if (!branchReview) return
			pageContent.structuredData.department[index].aggregateRating.reviewCount = branchReview.reviewCount
			pageContent.structuredData.department[index].aggregateRating.ratingValue = branchReview.currentRating
			pageContent.structuredData.department[index].aggregateRating.bestRating  = branchReview.maxRating
		})

		return {
			pageMeta,
			pageContent,
		}
	},
	data() {
		return {
			showMap: false,
		};
	},
	computed: {
		mapMarkerClass() {
			return `absolute z-30 text-goPink iconSize-40 transition-opacity duration-1000 ${this.showMap ? 'opacity-0' : 'opacity-100'}`
		},
		mapImage() {
			const tabletImgUrl = '/images/locations/car-rental-auckland-map_tablet.jpg'
			return {
				alt: "Map showing the location of the GO Rentals Auckland branches",
				mobile: { src: "/images/locations/car-rental-auckland-map_mobile.jpg", width: 428, height: 428, },
				tablet: { src: tabletImgUrl, width: 672, height: 672, },
				tabletWide: { src: tabletImgUrl, width: 456, height: 713, },
				desktop: { src: tabletImgUrl, width: 576, height: 576, },
				desktopWide: { src: tabletImgUrl, width: 656, height: 656, },
			};
		},
	},
	methods: {
		showGoogleMap() {
			this.showMap = true;
		},
	},
}
</script>

<style>
.map-marker1 {
	top: 37%; left: 30.5%;
}
.map-marker2 {
	top: 78%; left: 32.5%
}
.map-marker3 {
	top: 17.5%; left: 78.8%;
}

@media screen and (max-width: 699px) {
	.map-marker1 {
		top: 38.2%; left: 33%;
	}
	.map-marker2 {
		top: 70%; left: 34%
	}
	.map-marker3 {
		top: 23.5%; left: 70%;
	}
}
</style>
